// File Location: src/services/financialService.js

import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  onSnapshot,
  writeBatch
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "./firebaseConfig";
import { uploadFile } from './fileUploadService';
import { createNotification, sendEmail } from './notificationService';
import { sendFinancialApprovalPushNotification } from './pushNotificationService';
import { updateRoleBucket } from './bucketFilterService';
export const fetchCreditScore = async (panNumber, aadharNumber) => {
  try {
    // Mock API call - replace with actual credit bureau API
    const mockResponse = await new Promise(resolve => {
      setTimeout(() => {
        const mockScore = Math.floor(Math.random() * (850 - 300) + 300);
        const rating = mockScore >= 750 ? 'AAA' : 
                      mockScore >= 700 ? 'AA' : 
                      mockScore >= 650 ? 'A' : 
                      mockScore >= 600 ? 'BBB' : 'BB';
        
        resolve({
          score: mockScore,
          rating: rating,
          factors: [
            { name: 'Payment History', score: Math.floor(Math.random() * 100), impact: 'positive' },
            { name: 'Credit Utilization', score: Math.floor(Math.random() * 100), impact: 'neutral' },
            { name: 'Length of Credit History', score: Math.floor(Math.random() * 100), impact: 'positive' },
            { name: 'Credit Mix', score: Math.floor(Math.random() * 100), impact: 'positive' },
            { name: 'New Credit Inquiries', score: Math.floor(Math.random() * 100), impact: 'negative' }
          ],
          recommendations: [
            'Maintain low credit utilization ratio below 30%',
            'Pay all bills on time to improve payment history',
            'Avoid opening too many new credit accounts',
            'Consider increasing credit limits to improve utilization ratio'
          ]
        });
      }, 1500);
    });

    return {
      success: true,
      creditData: mockResponse
    };
  } catch (error) {
    console.error("Error fetching credit score:", error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Document Verification API Integration (Mock implementation)
export const verifyDocumentAuthenticity = async (documentUrl, documentType) => {
  try {
    // Mock API call - replace with actual document verification service
    const mockResponse = await new Promise(resolve => {
      setTimeout(() => {
        const confidence = Math.random();
        const verified = confidence > 0.7;
        const issues = [];
        
        if (!verified) {
          issues.push('Document quality too low');
          if (Math.random() > 0.5) issues.push('Suspicious alterations detected');
          if (Math.random() > 0.5) issues.push('Information mismatch');
        }
        
        resolve({
          verified: verified,
          confidence: confidence,
          issues: issues
        });
      }, 1000);
    });

    return mockResponse;
  } catch (error) {
    console.error("Error verifying document:", error);
    return {
      verified: false,
      confidence: 0,
      issues: ['Verification service unavailable']
    };
  }
};

// Financial Risk Assessment
export const calculateFinancialRisk = async (financialData) => {
  try {
    const { creditScore, income, employment, loanAmount, existingDebts } = financialData;
    
    let riskScore = 5; // Base risk score (1-10, where 10 is highest risk)
    let factors = [];
    
    // Credit Score Impact
    if (creditScore >= 750) {
      riskScore -= 2;
      factors.push({ name: 'Credit Score', level: 'low', description: 'Excellent credit score' });
    } else if (creditScore >= 650) {
      riskScore -= 1;
      factors.push({ name: 'Credit Score', level: 'medium', description: 'Good credit score' });
    } else {
      riskScore += 2;
      factors.push({ name: 'Credit Score', level: 'high', description: 'Poor credit score' });
    }
    
    // Income to Loan Ratio
    const incomeRatio = loanAmount / (income * 12);
    if (incomeRatio < 0.3) {
      riskScore -= 1;
      factors.push({ name: 'Income Ratio', level: 'low', description: 'Low loan-to-income ratio' });
    } else if (incomeRatio < 0.6) {
      factors.push({ name: 'Income Ratio', level: 'medium', description: 'Moderate loan-to-income ratio' });
    } else {
      riskScore += 2;
      factors.push({ name: 'Income Ratio', level: 'high', description: 'High loan-to-income ratio' });
    }
    
    // Employment Status
    if (employment === 'permanent') {
      riskScore -= 1;
      factors.push({ name: 'Employment', level: 'low', description: 'Stable employment' });
    } else if (employment === 'contract') {
      factors.push({ name: 'Employment', level: 'medium', description: 'Contract employment' });
    } else {
      riskScore += 1;
      factors.push({ name: 'Employment', level: 'high', description: 'Unstable employment' });
    }
    
    // Existing Debt Burden
    const debtRatio = existingDebts / (income * 12);
    if (debtRatio < 0.2) {
      factors.push({ name: 'Debt Burden', level: 'low', description: 'Low existing debt' });
    } else if (debtRatio < 0.4) {
      riskScore += 1;
      factors.push({ name: 'Debt Burden', level: 'medium', description: 'Moderate existing debt' });
    } else {
      riskScore += 2;
      factors.push({ name: 'Debt Burden', level: 'high', description: 'High existing debt' });
    }
    
    // Normalize risk score
    riskScore = Math.max(1, Math.min(10, riskScore));
    
    const overallRisk = riskScore <= 3 ? 'low' : riskScore <= 6 ? 'medium' : 'high';
    
    // Calculate recommended amount based on risk
    const maxLoanAmount = income * 12 * 0.6; // Max 60% of annual income
    const riskAdjustedAmount = Math.min(loanAmount, maxLoanAmount * (11 - riskScore) / 10);
    
    // Calculate suggested interest rate
    const baseRate = 8.5; // Base interest rate
    const riskPremium = (riskScore - 1) * 0.5; // 0.5% per risk point
    const suggestedInterestRate = baseRate + riskPremium;
    
    return {
      success: true,
      overallRisk: overallRisk,
      riskScore: riskScore,
      factors: factors,
      recommendedAmount: Math.round(riskAdjustedAmount),
      suggestedInterestRate: Math.round(suggestedInterestRate * 100) / 100,
      mitigationStrategies: generateMitigationStrategies(factors)
    };
  } catch (error) {
    console.error("Error calculating financial risk:", error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Generate risk mitigation strategies
function generateMitigationStrategies(factors) {
  const strategies = [];
  
  factors.forEach(factor => {
    if (factor.level === 'high') {
      switch (factor.name) {
        case 'Credit Score':
          strategies.push('Require additional guarantor or collateral');
          strategies.push('Implement stricter monitoring and follow-up');
          break;
        case 'Income Ratio':
          strategies.push('Reduce loan amount or extend repayment period');
          strategies.push('Require proof of additional income sources');
          break;
        case 'Employment':
          strategies.push('Request employment verification and salary certificates');
          strategies.push('Consider shorter loan tenure');
          break;
        case 'Debt Burden':
          strategies.push('Require debt consolidation plan');
          strategies.push('Lower loan amount to manage total debt burden');
          break;
      }
    }
  });
  
  return strategies;
}

// Save Financial Comments
export const saveFinancialComments = async (applicationId, dataOrUserId, commentsOrData, userName) => {
  try {
    // Handle both old and new parameter formats for backward compatibility
    let userId, comments, additionalData;
    
    if (typeof dataOrUserId === 'string' && typeof commentsOrData === 'string') {
      // Old format: (applicationId, userId, comments, userName)
      userId = dataOrUserId;
      comments = commentsOrData;
      additionalData = {};
    } else if (typeof dataOrUserId === 'object') {
      // New format: (applicationId, dataObject)
      additionalData = dataOrUserId;
      userId = additionalData.lastUpdatedBy || additionalData.userId;
      comments = additionalData.comments || '';
      userName = additionalData.userName || 'Financial Team';
    } else {
      return { success: false, error: "Invalid parameters provided" };
    }

    // Ensure userId is not undefined
    if (!userId) {
      console.warn("userId is undefined in saveFinancialComments, using 'system' as fallback");
      userId = 'system';
    }

    if (!applicationId) {
      return { success: false, error: "Application ID is required" };
    }

    const appQuery = query(
      collection(db, "patientApplications"),
      where("applicationId", "==", applicationId)
    );
    const appSnapshot = await getDocs(appQuery);

    if (appSnapshot.empty) {
      return { success: false, error: "Application not found" };
    }

    const appRef = doc(db, "patientApplications", appSnapshot.docs[0].id);
    const currentApp = appSnapshot.docs[0].data();

    // Handle internal comments
    if (comments && comments.trim()) {
      const existingComments = currentApp.workflow?.financialVerification?.internalComments || [];
      
      const newComment = {
        id: Date.now().toString(),
        userId: userId,
        userName: userName || "Financial Team",
        comments: comments.trim(),
        timestamp: new Date(),
        lastModified: new Date()
      };

      const existingCommentIndex = existingComments.findIndex(comment => comment.userId === userId);
      
      let updatedComments;
      if (existingCommentIndex >= 0) {
        updatedComments = [...existingComments];
        updatedComments[existingCommentIndex] = {
          ...updatedComments[existingCommentIndex],
          comments: comments.trim(),
          lastModified: new Date()
        };
      } else {
        updatedComments = [...existingComments, newComment];
      }

      await updateDoc(appRef, {
        "workflow.financialVerification.internalComments": updatedComments,
        "workflow.financialVerification.lastInternalCommentUpdate": new Date(),
      });
    }

    // Handle additional data (credit info, document status, assessment data)
    if (additionalData && typeof additionalData === 'object') {
      const updateData = {};
      
      // Save credit additional info
      if (additionalData.creditAdditionalInfo) {
        updateData["workflow.financialVerification.creditAdditionalInfo"] = additionalData.creditAdditionalInfo;
      }
      
      // Save document verification status
      if (additionalData.documentStatus) {
        updateData["workflow.financialVerification.documentStatus"] = additionalData.documentStatus;
      }
      
      // Save complete assessment data
      if (additionalData.assessmentData) {
        updateData["workflow.financialVerification.assessment"] = additionalData.assessmentData;
      }
      
      // Add metadata
      if (Object.keys(updateData).length > 0) {
        updateData["workflow.financialVerification.lastUpdatedBy"] = userId;
        updateData["workflow.financialVerification.lastUpdatedAt"] = new Date();
        
        if (additionalData.autoSaved) {
          updateData["workflow.financialVerification.autoSaved"] = true;
          updateData["workflow.financialVerification.lastAutoSaveAt"] = new Date();
        }
        
        if (additionalData.manualSave) {
          updateData["workflow.financialVerification.manualSaved"] = true;
          updateData["workflow.financialVerification.lastManualSaveAt"] = new Date();
        }
        
  await updateDoc(appRef, updateData);
      }
    }

    return { 
      success: true, 
      message: "Financial data saved successfully"
    };
  } catch (error) {
    console.error("Error saving financial data:", error);
    return { success: false, error: error.message };
  }
};

// Get Financial Comments and Additional Data
export const getFinancialComments = async (applicationId) => {
  try {
    if (!applicationId) {
      return { success: false, error: "Application ID is required" };
    }

    const appQuery = query(
      collection(db, "patientApplications"),
      where("applicationId", "==", applicationId)
    );
    const appSnapshot = await getDocs(appQuery);

    if (appSnapshot.empty) {
      return { success: false, error: "Application not found" };
    }

    const appData = appSnapshot.docs[0].data();
    const financialVerification = appData.workflow?.financialVerification || {};
    
    // Get internal comments
    const internalComments = financialVerification.internalComments || [];
    const sortedComments = internalComments.sort((a, b) => {
      const timeA = a.timestamp?.toDate?.() || new Date(a.timestamp);
      const timeB = b.timestamp?.toDate?.() || new Date(b.timestamp);
      return timeB - timeA;
    });

    // Combine all financial data for easy access
    const financialData = {
      comments: sortedComments,
      creditAdditionalInfo: financialVerification.creditAdditionalInfo || {
        customCreditScore: '',
        creditBureauName: 'CIBIL',
        creditReportDate: '',
        previousCreditHistory: '',
        defaultHistory: '',
        additionalNotes: [],
        customFields: []
      },
      documentStatus: financialVerification.documentStatus || {},
      assessment: financialVerification.assessment || {},
      lastUpdatedBy: financialVerification.lastUpdatedBy,
      lastUpdatedAt: financialVerification.lastUpdatedAt,
      autoSaved: financialVerification.autoSaved,
      lastAutoSaveAt: financialVerification.lastAutoSaveAt,
      manualSaved: financialVerification.manualSaved,
      lastManualSaveAt: financialVerification.lastManualSaveAt
    };

    return { 
      success: true, 
      ...financialData
    };
  } catch (error) {
    console.error("Error getting financial data:", error);
    return { 
      success: false, 
      error: error.message, 
      comments: [],
      creditAdditionalInfo: {
        customCreditScore: '',
        creditBureauName: 'CIBIL',
        creditReportDate: '',
        previousCreditHistory: '',
        defaultHistory: '',
        additionalNotes: [],
        customFields: []
      },
      documentStatus: {},
      assessment: {}
    };
  }
};

// Get Financial History
export const getFinancialHistory = async (applicationId) => {
  try {
    const appQuery = query(
      collection(db, "patientApplications"),
      where("applicationId", "==", applicationId)
    );
    const appSnapshot = await getDocs(appQuery);

    if (appSnapshot.empty) {
      return { success: false, error: "Application not found" };
    }

    const appData = appSnapshot.docs[0].data();
    const financialHistory = appData.patient?.financialHistory || {
      previousLoans: [],
      creditUtilization: 0,
      repaymentHistory: [],
      defaults: []
    };

    return { 
      success: true, 
      history: financialHistory
    };
  } catch (error) {
    console.error("Error getting financial history:", error);
    return { success: false, error: error.message };
  }
};

// Update Financial Status
export const updateFinancialStatus = async (applicationId, statusData) => {
  try {
    // Guard: Prevent setting terminal 'approved' state via this helper
    // Approval must go through completeFinancialVerification() which applies dispute gating
    if (statusData && typeof statusData === 'object') {
      const desired = (statusData.status || '').toLowerCase();
      if (desired === 'approved') {
        return {
          success: false,
          error: "Direct approval via updateFinancialStatus is not allowed. Use completeFinancialVerification() to finalize approvals.",
          code: 'INVALID_FLOW'
        };
      }
    }

    const appQuery = query(
      collection(db, "patientApplications"),
      where("applicationId", "==", applicationId)
    );
    const appSnapshot = await getDocs(appQuery);

    if (appSnapshot.empty) {
      return { success: false, error: "Application not found" };
    }
    const appRef = doc(db, "patientApplications", appSnapshot.docs[0].id);
    const currentApp = appSnapshot.docs[0].data();

    // Safety: Do not allow downgrading financial status after approval or when downstream phases have started
    const desired = (statusData?.status || '').toLowerCase();
    const currentFin = (currentApp?.workflow?.financialVerification?.status || '').toLowerCase();
    const topLevel = (currentApp?.status || '').toLowerCase();
    const disbursalStatus = (currentApp?.workflow?.disbursal?.status || '').toLowerCase();
    const dischargeStatus = (currentApp?.workflow?.dischargeSettlement?.status || '').toLowerCase();
    const postFinanceLocked = (
      topLevel === 'financial_approved' ||
      topLevel === 'financial_rejected' ||
      topLevel === 'pending_financial_clarification' ||
      topLevel === 'settlement_approved' ||
      topLevel === 'ready_for_disbursal' ||
      topLevel === 'amount_disbursed' ||
      topLevel === 'case_closed' ||
      currentFin === 'approved' ||
      dischargeStatus === 'approved' ||
      (disbursalStatus && disbursalStatus !== 'pending')
    );
    if (postFinanceLocked && desired && desired !== 'approved') {
      return {
        success: true,
        message: `Ignoring financial status update to '${desired}' because application is post-finance locked (current: topLevel=${topLevel}, fin=${currentFin}).`,
        code: 'NO_OP_POST_FINANCE'
      };
    }

    const updateData = {};
    Object.keys(statusData).forEach(key => {
      const value = statusData[key];
      // Firestore rejects undefined values; skip keys with undefined
      if (value !== undefined) {
        updateData[`workflow.financialVerification.${key}`] = value;
      }
    });

    await updateDoc(appRef, updateData);

    return { success: true, message: "Financial status updated successfully" };
  } catch (error) {
    console.error("Error updating financial status:", error);
    return { success: false, error: error.message };
  }
};

// Get financial team dashboard statistics
export const getFinancialTeamStats = async (financialTeamId) => {
  try {
    // Get pending applications for financial processing
    const pendingQuery = query(
      collection(db, "patientApplications"),
      where("workflow.policyVerification.status", "==", "approved"),
      where("workflow.financialVerification.status", "in", ["pending", null])
    );
    const pendingSnapshot = await getDocs(pendingQuery);

    // Get applications processed by this financial team member
    const processedQuery = query(
      collection(db, "patientApplications"),
      where("workflow.financialVerification.verifiedBy", "==", financialTeamId)
    );
    const processedSnapshot = await getDocs(processedQuery);

    // Get today's processing
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTimestamp = Timestamp.fromDate(today);

    let processedToday = 0;
    let totalProcessingTime = 0;
    let approvedCount = 0;
    let rejectedCount = 0;
    let pendingInfoCount = 0;
    let emergencyPending = 0;
    let totalDisbursedAmount = 0;
    let totalClaimAmount = 0;
  let disbursalsPending = 0;
    let recoveryPending = 0;
  let enachPending = 0;

    // Count emergency cases in pending
    pendingSnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.emergency) {
        emergencyPending++;
      }
    });

    // Calculate stats from processed applications
    processedSnapshot.forEach((doc) => {
      const data = doc.data();
      const processedAt = data.workflow?.financialVerification?.verifiedAt;

      if (processedAt && processedAt >= todayTimestamp) {
        processedToday++;
      }

      if (data.workflow?.financialVerification?.processingTimeMinutes) {
        totalProcessingTime += data.workflow.financialVerification.processingTimeMinutes;
      }

      const status = data.workflow?.financialVerification?.status;
      if (status === "approved") {
        approvedCount++;
        totalDisbursedAmount += data.workflow?.financialVerification?.finalDisbursalAmount || 0;
      } else if (status === "rejected") {
        rejectedCount++;
      } else if (status === "pending_info") {
        pendingInfoCount++;
      }

      // Check disbursal status
      if (data.workflow?.disbursal?.status === "pending") {
        disbursalsPending++;
      }

      // eNach pending: FAL accepted & eNach not completed
      const dsAck = data.workflow?.dischargeSettlement?.hospitalAcknowledgement?.status === 'accepted';
      const enachStatus = data.workflow?.enach?.status;
      if (dsAck && enachStatus !== 'completed') {
        enachPending++;
      }

      // Check recovery status
      if (data.workflow?.recovery?.status === "pending") {
        recoveryPending++;
      }

      totalClaimAmount += data.medical?.actualCost || data.medical?.estimatedCost || 0;
    });

    const averageProcessingTime = processedSnapshot.size > 0
      ? Math.round(totalProcessingTime / processedSnapshot.size)
      : 0;

    const approvalRate = processedSnapshot.size > 0
      ? Math.round((approvedCount / processedSnapshot.size) * 100)
      : 0;

    return {
      success: true,
      stats: {
        pendingProcessing: pendingSnapshot.size,
        processedToday,
        totalProcessed: processedSnapshot.size,
        averageProcessingTime,
        emergencyPending,
        approvalRate,
        approvedCount,
        rejectedCount,
        pendingInfoCount,
        totalDisbursedAmount,
        totalClaimAmount,
        disbursalsPending,
        recoveryPending,
        enachPending,
        averageDisbursalRatio: totalClaimAmount > 0 
          ? Math.round((totalDisbursedAmount / totalClaimAmount) * 100)
          : 0
      }
    };
  } catch (error) {
    console.error("Error getting financial team stats:", error);
    return {
      success: false,
      error: error.message,
      stats: {
        pendingProcessing: 0,
        processedToday: 0,
        totalProcessed: 0,
        averageProcessingTime: 0,
        emergencyPending: 0,
        approvalRate: 0,
        approvedCount: 0,
        rejectedCount: 0,
        pendingInfoCount: 0,
        totalDisbursedAmount: 0,
        totalClaimAmount: 0,
        disbursalsPending: 0,
        recoveryPending: 0,
        enachPending: 0,
        averageDisbursalRatio: 0
      }
    };
  }
};

// Get applications pending financial verification
export const getFinancialApplications = async (filters = {}, page = 1, pageSize = 10) => {
  try {
    let q = collection(db, "patientApplications");

    // Build base filters depending on requested context/status
    // If an explicit status is requested (e.g., "settlement_approved" for disbursal),
    // don't constrain to financial verification pending queue.
    if (filters.status && filters.status !== "") {
      q = query(q, where("status", "==", filters.status));
    } else {
      // Default queue selection
      // If explicitly fetching for settlement, do not constrain financial verification status,
      // as some apps may not have the field set yet (missing field won't match Firestore 'in').
      if (filters.forSettlement) {
        // Show any application where doctor completed discharge review
        // Avoid status constraints so finance can see cases irrespective of main status
        q = query(
          q,
          where("workflow.medicalVerification.dischargeReviewCompleted", "==", true)
        );
      } else if (filters._fetchAll) {
        // For Pending Data page, fetch all applications and filter client-side
        // No status constraint - we'll filter for finance-relevant apps client-side
      } else {
        // Financial verification default queue (pending or null)
        // Include medical_approved applications even if financialVerification is not yet initialized
        q = query(
          q,
          where("status", "in", ["medical_approved", "approved_for_process"])
        );
      }
    }

    // Apply filters similar to policy applications
  // Note: status filter is already applied above when provided

    if (filters.priority && filters.priority !== "") {
      q = query(q, where("priority", "==", filters.priority));
    }

    if (filters.emergency !== "" && filters.emergency !== undefined) {
      const isEmergency = filters.emergency === "true";
      q = query(q, where("emergency", "==", isEmergency));
    }

    if (filters.treatmentType && filters.treatmentType !== "") {
      q = query(q, where("medical.treatmentType", "==", filters.treatmentType));
    }

    if (filters.insuranceCompany && filters.insuranceCompany !== "") {
      q = query(q, where("policy.insuranceCompany", "==", filters.insuranceCompany));
    }

    if (filters.hospitalName && filters.hospitalName !== "") {
      q = query(q, where("hospitalName", "==", filters.hospitalName));
    }

    // Order by priority and submission date (skip for settlement view to reduce index requirements)
    if (!filters.forSettlement) {
      q = query(q, orderBy("emergency", "desc"), orderBy("submittedAt", "desc"));
    }

    const querySnapshot = await getDocs(q);
    let applications = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      applications.push({
        id: doc.id,
        ...data
      });
    });

    // Client-side filtering for finance-relevant applications
    // Only include applications that are relevant to finance team
    const originalCount = applications.length;
    applications = applications.filter(app => {
      const w = app.workflow || {};
      const fin = w.financialVerification || {};
      const ds = w.dischargeSettlement || {};
      const mv = w.medicalVerification || {};
      
      // INCLUDE: Applications that are in finance pipeline
      const isFinanceRelevant = (
        // Medical approved and entering finance pipeline
        (mv.status === 'approved' || app.status === 'medical_approved') ||
        // Has financial verification workflow
        (fin.status || fin.assignedTo || fin.createdAt || fin.underReview || fin.assessmentStarted || fin.documentReview) ||
        // Has discharge settlement workflow
        (ds.status || ds.finalApprovalLetterUrl || ds.finalApprovalLetterId || ds.createdAt) ||
        // Has disbursal workflow
        (w.disbursal?.status || w.disbursal?.createdAt) ||
        // Has eNACH workflow
        (w.enach?.status || w.enach?.createdAt || w.eNach?.status || w.eNach?.createdAt) ||
        // Has POD workflow
        (w.proofOfDispatch?.status || w.proofOfDispatch?.createdAt || w.pod?.status || w.pod?.createdAt) ||
        // Has recovery workflow
        (w.recovery?.status || w.recovery?.createdAt) ||
        // Finance-specific statuses
        [
          'financial_approved', 'financial_rejected', 'pending_financial_clarification',
          'amount_disbursed', 'under_financial_review', 'financial_review_pending',
          'financial_assessment', 'document_verification', 'credit_analysis',
          'under_financial_assessment', 'financial_document_review',
          'awaiting_financial_approval', 'financial_verification_pending',
          'settlement_approved', 'dispute_pending', 'approved_for_process'
        ].includes(app.status) ||
        // Also include applications that have any finance-related workflow activity
        (fin.createdAt || fin.assignedTo || fin.underReview || fin.assessmentStarted || fin.documentReview) ||
        (ds.createdAt || ds.finalApprovalLetterUrl || ds.finalApprovalLetterId) ||
        (w.disbursal?.createdAt || w.enach?.createdAt || w.eNach?.createdAt || 
         w.proofOfDispatch?.createdAt || w.pod?.createdAt || w.recovery?.createdAt)
      );
      
      // EXCLUDE: Non-finance categories
      const isNonFinance = (
        // Medical-only (before finance stage)
        (app.status === 'submitted' || app.status === 'medical_review_pending' ||
         app.status === 'under_medical_review' || app.status === 'medical_rejected' ||
         app.status === 'diagnosis_approved') ||
        // Hospital policy applications
        (app.status === 'hospital_policy_review' || app.status === 'policy_approved' ||
         app.status === 'policy_rejected') ||
        // Doctor-only applications
        (app.status === 'doctor_review_pending' || app.status === 'under_doctor_review' ||
         app.status === 'doctor_approved' || app.status === 'doctor_rejected')
      );
      
      // For Pending Data page, be more permissive to catch finance-relevant applications
      if (filters._fetchAll) {
        // EXCLUDE: Fully approved applications from pending data
        const isFullyApproved = (
          (fin.status === 'approved' && w.disbursal?.status === 'completed') ||
          (fin.status === 'approved' && w.disbursal?.status === 'disbursed') ||
          (app.status === 'financial_approved' && w.disbursal?.status === 'completed') ||
          (app.status === 'financial_approved' && w.disbursal?.status === 'disbursed') ||
          (app.status === 'completed' || app.status === 'disbursed') ||
          (w.disbursal?.status === 'completed' && w.disbursal?.status === 'disbursed')
        );
        
        if (isFullyApproved) return false;
        
        // More permissive filtering for Pending Data page
        return isFinanceRelevant || (
          // Include any application that might be finance-relevant
          app.status === 'medical_approved' ||
          app.status === 'approved_for_process' ||
          app.status === 'under_review' ||
          app.status === 'pending_documents' ||
          // Has any workflow activity
          (w.financialVerification && Object.keys(w.financialVerification).length > 0) ||
          (w.dischargeSettlement && Object.keys(w.dischargeSettlement).length > 0) ||
          (w.disbursal && Object.keys(w.disbursal).length > 0) ||
          (w.enach && Object.keys(w.enach).length > 0) ||
          (w.eNach && Object.keys(w.eNach).length > 0) ||
          (w.proofOfDispatch && Object.keys(w.proofOfDispatch).length > 0) ||
          (w.pod && Object.keys(w.pod).length > 0) ||
          (w.recovery && Object.keys(w.recovery).length > 0)
        );
      }
      
      return isFinanceRelevant && !isNonFinance;
    });
    
    console.log(`🔍 Financial service filtering: ${originalCount} → ${applications.length} applications`);
    
    // Debug: Log sample applications to understand the data structure
    if (applications.length > 0) {
      console.log('📋 Sample finance application:', {
        id: applications[0].id,
        status: applications[0].status,
        workflow: applications[0].workflow,
        financialVerification: applications[0].workflow?.financialVerification,
        medicalVerification: applications[0].workflow?.medicalVerification
      });
    } else {
      console.log('⚠️ No finance applications found after filtering');
    }

    // Client-side filtering for search term and date ranges
    if (filters.searchTerm && filters.searchTerm.trim() !== "") {
      const searchTerm = filters.searchTerm.toLowerCase();
      applications = applications.filter(app =>
        app.applicationId?.toLowerCase().includes(searchTerm) ||
        app.patient?.name?.toLowerCase().includes(searchTerm) ||
        app.policy?.number?.toLowerCase().includes(searchTerm) ||
        app.hospitalName?.toLowerCase().includes(searchTerm)
      );
    }

    // Apply date filters
    if (filters.dateFrom && filters.dateFrom !== "") {
      const fromDate = new Date(filters.dateFrom);
      applications = applications.filter(app => {
        const submittedDate = app.submittedAt?.toDate ? app.submittedAt.toDate() : new Date(app.submittedAt);
        return submittedDate >= fromDate;
      });
    }

    if (filters.dateTo && filters.dateTo !== "") {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      applications = applications.filter(app => {
        const submittedDate = app.submittedAt?.toDate ? app.submittedAt.toDate() : new Date(app.submittedAt);
        return submittedDate <= toDate;
      });
    }

    // Apply amount range filters
    if (filters.minAmount && filters.minAmount !== "") {
      const minAmount = parseFloat(filters.minAmount);
      applications = applications.filter(app =>
        (app.workflow?.policyVerification?.approvedCoverageAmount || 0) >= minAmount
      );
    }

    if (filters.maxAmount && filters.maxAmount !== "") {
      const maxAmount = parseFloat(filters.maxAmount);
      applications = applications.filter(app =>
        (app.workflow?.policyVerification?.approvedCoverageAmount || 0) <= maxAmount
      );
    }

    // Pagination
    const total = applications.length;
    const startIndex = (page - 1) * pageSize;
    const paginatedApplications = applications.slice(startIndex, startIndex + pageSize);

    return {
      success: true,
      applications: paginatedApplications,
      total,
      page,
      pageSize,
      totalPages: Math.ceil(total / pageSize)
    };
  } catch (error) {
    console.error("Error getting financial applications:", error);
    return {
      success: false,
      error: error.message,
      applications: [],
      total: 0
    };
  }
};

// Complete financial verification (KYC and eligibility)
export const completeFinancialVerification = async (applicationId, financialTeamId, verificationData) => {
  try {
    // Check for unresolved queries before allowing approval
    const queryCheck = await canApplicationProceedToFinancial(applicationId);
    if (!queryCheck.canProceed) {
      return {
        success: false,
        error: `Cannot complete financial verification: ${queryCheck.reason}`,
        queryInfo: {
          hasUnresolvedQueries: queryCheck.reason.includes("unresolved queries"),
          unresolvedQueries: queryCheck.unresolvedQueries || [],
          queryCount: queryCheck.queryCount || 0
        }
      };
    }

    const appQuery = query(
      collection(db, "patientApplications"),
      where("applicationId", "==", applicationId)
    );
    const appSnapshot = await getDocs(appQuery);

    if (appSnapshot.empty) {
      return { success: false, error: "Application not found" };
    }

  const appDoc = appSnapshot.docs[0];
  const appData = appDoc.data();
    const appRef = doc(db, "patientApplications", appDoc.id);

    // Calculate processing time
    const processingStartTime = verificationData.processingStartTime || new Date();
    const processingEndTime = new Date();
    const processingTimeMinutes = Math.round((processingEndTime - processingStartTime) / (1000 * 60));

    const batch = writeBatch(db);

    // Update application with financial verification
    // Build updateData while omitting any undefined values (Firestore rejects undefined)
    const updateData = {
      'workflow.financialVerification.status': verificationData.status, // approved, rejected, pending_info
      'workflow.financialVerification.verifiedBy': financialTeamId,
      'workflow.financialVerification.verifiedAt': Timestamp.now(),
      'workflow.financialVerification.comments': verificationData.comments || '',
      'workflow.financialVerification.processingTimeMinutes': processingTimeMinutes,
      'workflow.financialVerification.finalDisbursalAmount': verificationData.finalDisbursalAmount || 0,
      updatedAt: Timestamp.now()
    };
    // Optional fields
    if (verificationData.kycDetails !== undefined) {
      updateData['workflow.financialVerification.kycDetails'] = verificationData.kycDetails;
    }
    if (verificationData.creditScore !== undefined) {
      updateData['workflow.financialVerification.creditScore'] = verificationData.creditScore;
    }
    if (verificationData.eligibilityScore !== undefined) {
      updateData['workflow.financialVerification.eligibilityScore'] = verificationData.eligibilityScore;
    }
    if (verificationData.riskAssessment !== undefined) {
      updateData['workflow.financialVerification.riskAssessment'] = verificationData.riskAssessment;
    }
    if (verificationData.lenderDetails !== undefined) {
      updateData['workflow.financialVerification.lenderDetails'] = verificationData.lenderDetails;
    }

    // Update main status based on verification result
    if (verificationData.status === "approved") {
      updateData.status = "financial_approved";
      // Initialize disbursal workflow
      updateData['workflow.disbursal'] = {
        status: "pending",
        createdAt: Timestamp.now()
      };
    } else if (verificationData.status === "rejected") {
      updateData.status = "financial_rejected";
    } else if (verificationData.status === "pending_info") {
      updateData.status = "pending_financial_clarification";
    }

    // If KYP was challenged and reassigned to Finance, mark progress for reassignment tracking
    try {
      const kypAckStatus = appData?.workflow?.medicalVerification?.kypHospitalAcknowledgement?.status;
      const reassignment = appData?.workflow?.medicalVerification?.kypReassignment;
      const targetsArr = reassignment?.targets || (reassignment?.target ? [reassignment.target] : []);
      const includesFinance = targetsArr.includes('finance') || reassignment?.target === 'finance' || reassignment?.target === 'both';

      if (kypAckStatus === 'challenged' && includesFinance) {
        updateData['workflow.medicalVerification.kypReassignment.financeCompletedAt'] = new Date();
        if (targetsArr.includes('policy')) {
          updateData['workflow.medicalVerification.kypReassignment.status'] = 'finance_completed';
        } else {
          updateData['workflow.medicalVerification.kypReassignment.status'] = 'completed';
        }
      }
    } catch (err) {
      console.warn('Error preparing finance reassignment progress update:', err);
    }

    batch.update(appRef, updateData);

    // Create financial verification record
    const financialVerificationRef = doc(collection(db, "financialVerifications"));
    // Build a sanitized record payload without undefined fields
    const verificationRecord = {
      applicationId,
      financialTeamId,
      status: verificationData.status,
      verifiedAt: Timestamp.now(),
      createdAt: Timestamp.now()
    };
    if (verificationData.kycDetails !== undefined) verificationRecord.kycDetails = verificationData.kycDetails;
    if (verificationData.creditScore !== undefined) verificationRecord.creditScore = verificationData.creditScore;
    if (verificationData.eligibilityScore !== undefined) verificationRecord.eligibilityScore = verificationData.eligibilityScore;
    if (verificationData.riskAssessment !== undefined) verificationRecord.riskAssessment = verificationData.riskAssessment;
    if (verificationData.finalDisbursalAmount !== undefined) verificationRecord.finalDisbursalAmount = verificationData.finalDisbursalAmount;
    if (verificationData.lenderDetails !== undefined) verificationRecord.lenderDetails = verificationData.lenderDetails;
    if (verificationData.comments !== undefined) verificationRecord.comments = verificationData.comments;

    batch.set(financialVerificationRef, verificationRecord);

    await batch.commit();

    // Update bucket filters
    if (verificationData.status === "approved") {
      await updateRoleBucket(appDoc.id, 'finance', 'financialApproved');
      // If both policy and finance approved, doctor bucket becomes kyp_pending
      const policyApproved = appData?.workflow?.policyVerification?.status === 'approved' ||
                            appData?.workflow?.policyVerification?.status === 'completed';
      if (policyApproved) {
        await updateRoleBucket(appDoc.id, 'doctor', 'kyp_pending');
      }
    } else if (verificationData.status === "rejected") {
      await updateRoleBucket(appDoc.id, 'finance', 'query');
      await updateRoleBucket(appDoc.id, 'doctor', 'rejected');
    }

    // Cross-approval email notification: If Finance just approved and Policy already approved, notify Doctors team
    try {
      if ((verificationData.status || '').toLowerCase() === 'approved') {
        const policyApproved = (
          (appData?.workflow?.policyVerification?.status || '').toLowerCase() === 'approved' ||
          (appData?.status || '').toLowerCase() === 'policy_approved'
        );
        if (policyApproved && typeof sendEmail === 'function') {
          const patientName = appData?.patient?.name || 'Patient';
          const appIdStr = appData?.applicationId || applicationId;
          const subject = `Application ready for KYP – ${patientName} (ID: ${appIdStr})`;
          const html = `<!DOCTYPE html><html><body style=\"font-family:Arial,Helvetica,sans-serif;line-height:1.6;color:#0f172a;\">\n            <h2 style=\"margin:0 0 12px;color:#065f46;\">Application Ready for KYP</h2>\n            <p style=\"margin:0 0 8px;\">The application <strong>${appIdStr}</strong> for <strong>${patientName}</strong> now has <strong>both Policy and Finance approvals</strong>.</p>\n            <p style=\"margin:0 0 12px;\"><strong>This application is ready for KYP process — please generate KYP.</strong></p>\n            <p style=\"margin:0 0 8px;\"><a href=\"${typeof window === 'undefined' ? '' : ''}/doctor/application-review/${appIdStr}\" style=\"background:#2563eb;color:#fff;padding:8px 12px;border-radius:6px;text-decoration:none;display:inline-block;\">Open to Generate KYP</a></p>\n            <p style=\"margin:12px 0 0;font-size:12px;color:#64748b;\">This is an automated notification.</p>\n          </body></html>`;
          await sendEmail({ to: 'doctors@samhealth.co.in', subject, html });

          // Send push notification
          await sendFinancialApprovalPushNotification(appIdStr, patientName);
        }
      }
    } catch (emailErr) {
      console.warn('Cross-approval doctor email (finance) failed:', emailErr?.message || emailErr);
    }

    // Notify the doctor to revise KYP if this was a challenged & reassigned case
    try {
      const kypAckStatus = appData?.workflow?.medicalVerification?.kypHospitalAcknowledgement?.status;
      const reassignment = appData?.workflow?.medicalVerification?.kypReassignment;
      const targetsArr = reassignment?.targets || (reassignment?.target ? [reassignment.target] : []);
      const includesFinance = targetsArr.includes('finance') || reassignment?.target === 'finance' || reassignment?.target === 'both';

      if (kypAckStatus === 'challenged' && includesFinance) {
        const doctorId = appData?.workflow?.medicalVerification?.verifiedBy || 'medical';
        const patientName = appData?.patient?.name || 'Patient';
        const appIdStr = appData?.applicationId || applicationId;
        await createNotification({
          recipientId: doctorId,
          recipientType: doctorId === 'medical' ? 'department' : 'user',
          type: 'application',
          subType: 'kyp_revision_requested',
          title: 'Finance verification updated for KYP challenge',
          message: `Finance team has updated verification for challenged KYP of ${patientName} (ID: ${appIdStr}). Please review and revise the KYP if needed.`,
          applicationId: appIdStr,
          actionUrl: `/doctor/application-review/${appIdStr}`,
          priority: 'high',
          metadata: {
            patientName,
            reassignmentTargets: targetsArr,
            source: 'finance'
          }
        });
      }
    } catch (notifyErr) {
      console.warn('Finance completion notification failed:', notifyErr);
    }

    return {
      success: true,
      message: "Financial verification completed successfully"
    };
  } catch (error) {
    console.error("Error completing financial verification:", error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Process discharge summary and calculate final amount
export const processDischargeSettlement = async (applicationId, financialTeamId, settlementData) => {
  try {
    const appQuery = query(
      collection(db, "patientApplications"),
      where("applicationId", "==", applicationId)
    );
    const appSnapshot = await getDocs(appQuery);

    if (appSnapshot.empty) {
      return { success: false, error: "Application not found" };
    }

    const appDoc = appSnapshot.docs[0];
    const appRef = doc(db, "patientApplications", appDoc.id);

    const batch = writeBatch(db);

    // Update application with discharge settlement
    const updateData = {
      'workflow.dischargeSettlement': {
        status: settlementData.status, // approved, rejected, pending_adjustment
        processedBy: financialTeamId,
        processedAt: Timestamp.now(),
        dischargeItems: settlementData.dischargeItems,
        totalActualCost: settlementData.totalActualCost,
        approvedAmount: settlementData.approvedAmount,
        deductions: settlementData.deductions,
        finalSettlementAmount: settlementData.finalSettlementAmount,
        adjustments: settlementData.adjustments || [],
        comments: settlementData.comments
      },
      'medical.actualCost': settlementData.totalActualCost,
      updatedAt: Timestamp.now()
    };

    if (settlementData.status === "approved") {
      updateData.status = "settlement_approved";
      updateData['workflow.disbursal.status'] = "ready_for_disbursal";
      updateData['workflow.disbursal.finalAmount'] = settlementData.finalSettlementAmount;
    }

    batch.update(appRef, updateData);

    // Create discharge settlement record
    const settlementRef = doc(collection(db, "dischargeSettlements"));
    batch.set(settlementRef, {
      applicationId,
      processedBy: financialTeamId,
      processedAt: Timestamp.now(),
      ...settlementData,
      createdAt: Timestamp.now()
    });

    await batch.commit();

    return {
      success: true,
      message: "Discharge settlement processed successfully"
    };
  } catch (error) {
    console.error("Error processing discharge settlement:", error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Manage disbursal process
export const manageDisbursalProcess = async (applicationId, financialTeamId, disbursalData) => {
  try {
    const appQuery = query(
      collection(db, "patientApplications"),
      where("applicationId", "==", applicationId)
    );
    const appSnapshot = await getDocs(appQuery);

    if (appSnapshot.empty) {
      return { success: false, error: "Application not found" };
    }

    const appDoc = appSnapshot.docs[0];
    const appRef = doc(db, "patientApplications", appDoc.id);

    const batch = writeBatch(db);

    // Normalize/augment disbursal payload
    const normalizedDisbursal = { ...disbursalData };
    // Convert disbursedDate to Timestamp if it's a string from input (date or datetime-local)
    if (normalizedDisbursal.disbursedDate) {
      const dt = normalizedDisbursal.disbursedDate?.toDate
        ? normalizedDisbursal.disbursedDate.toDate()
        : new Date(normalizedDisbursal.disbursedDate);
      if (!isNaN(dt.getTime())) {
        normalizedDisbursal.disbursedDate = Timestamp.fromDate(dt);
      }
    }

    // Update disbursal status
    const updateData = {
      'workflow.disbursal': {
        ...normalizedDisbursal,
        updatedBy: financialTeamId,
        updatedAt: Timestamp.now()
      },
      updatedAt: Timestamp.now()
    };

  if (normalizedDisbursal.status === "disbursed") {
      updateData.status = "amount_disbursed";
      // Initialize recovery tracking
      updateData['workflow.recovery'] = {
        status: "pending",
        startedAt: Timestamp.now(),
    lenderDetails: normalizedDisbursal.lenderDetails,
    insuranceClaimDetails: normalizedDisbursal.insuranceClaimDetails
      };
    }

    batch.update(appRef, updateData);

    // Update bucket filter based on disbursal status
    const { updateRoleBucket } = await import('./bucketFilterService');
    if (normalizedDisbursal.status === "disbursed") {
      await updateRoleBucket(appDoc.id, 'finance', 'disbursed');
    } else if (normalizedDisbursal.status === "ready_for_disbursal") {
      await updateRoleBucket(appDoc.id, 'finance', 'readyForDisbursal');
    }

    // Create disbursal record
    const disbursalRef = doc(collection(db, "disbursals"));
    batch.set(disbursalRef, {
      applicationId,
      managedBy: financialTeamId,
      createdAt: Timestamp.now(),
      ...normalizedDisbursal
    });

    await batch.commit();

    return {
      success: true,
      message: "Disbursal process updated successfully"
    };
  } catch (error) {
    console.error("Error managing disbursal process:", error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Track recovery and insurance claim status
export const updateRecoveryStatus = async (applicationId, financialTeamId, recoveryData) => {
  try {
    const appQuery = query(
      collection(db, "patientApplications"),
      where("applicationId", "==", applicationId)
    );
    const appSnapshot = await getDocs(appQuery);

    if (appSnapshot.empty) {
      return { success: false, error: "Application not found" };
    }

    const appDoc = appSnapshot.docs[0];
    const appRef = doc(db, "patientApplications", appDoc.id);

    const batch = writeBatch(db);

    // Update recovery status
    const updateData = {
      'workflow.recovery': {
        ...recoveryData,
        updatedBy: financialTeamId,
        updatedAt: Timestamp.now()
      },
      updatedAt: Timestamp.now()
    };

    if (recoveryData.status === "completed") {
      updateData.status = "case_closed";
      updateData['workflow.recovery.completedAt'] = Timestamp.now();
    }

    batch.update(appRef, updateData);

    // Update bucket filter based on recovery status
    const { updateRoleBucket } = await import('./bucketFilterService');
    if (recoveryData.status === "pending") {
      await updateRoleBucket(appDoc.id, 'finance', 'recoveryPending');
    } else if (recoveryData.status === "completed") {
      await updateRoleBucket(appDoc.id, 'finance', 'caseClosed');
    }

    // Create recovery tracking record
    const recoveryRef = doc(collection(db, "recoveryTracking"));
    batch.set(recoveryRef, {
      applicationId,
      updatedBy: financialTeamId,
      updatedAt: Timestamp.now(),
      ...recoveryData,
      createdAt: Timestamp.now()
    });

    await batch.commit();

    return {
      success: true,
      message: "Recovery status updated successfully"
    };
  } catch (error) {
    console.error("Error updating recovery status:", error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Generate disbursal letter

// =============================
// eNach Workflow Helpers
// =============================
// Schema added under application.workflow.enach
// {
//   status: 'initiated' | 'completed',
//   submittedAt, submittedBy, link?, comments?,
//   hospitalCompletedAt?, hospitalCompletedBy?
// }

export const getENachQueue = async () => {
  try {
    const appsRef = collection(db, 'patientApplications');
    // Primary query uses legacy acceptance field (Firestore cannot OR with dynamic map key)
    const q = query(appsRef, where('workflow.dischargeSettlement.hospitalAcknowledgement.status', '==', 'accepted'));
    const snap = await getDocs(q);
    const items = [];
    snap.forEach(d => {
      const data = d.data();
      const enach = data.workflow?.enach || {};
      if (enach.status !== 'completed') items.push({ id: d.id, ...data });
    });

    // Fallback: fetch additional candidates where legacy field missing but new acknowledgements map contains accepted FAL
    // (We perform a second broad fetch limited to recent updated documents and filter client-side to avoid heavy reads.)
    const recentQ = query(appsRef, orderBy('updatedAt', 'desc'), limit(50));
    const recentSnap = await getDocs(recentQ);
    recentSnap.forEach(d => {
      const data = d.data();
      // Skip those already included by legacy path
      if (data?.workflow?.dischargeSettlement?.hospitalAcknowledgement?.status === 'accepted') return;
      const falId = data?.workflow?.dischargeSettlement?.finalApprovalLetterId;
      const falUrl = data?.workflow?.dischargeSettlement?.finalApprovalLetterUrl;
      const ack = falId && data?.workflow?.acknowledgements?.[falId];
      const isAccepted = Boolean(falUrl && ack?.status === 'accepted');
      const enach = data.workflow?.enach || {};
      if (isAccepted && enach.status !== 'completed') {
        // Avoid duplicates by checking existing list
        if (!items.find(x => x.applicationId === data.applicationId)) {
          items.push({ id: d.id, ...data });
        }
      }
    });

    return { success: true, items };
  } catch (error) {
    console.error('Error loading eNach queue:', error);
    return { success: false, error: error.message };
  }
};

export const submitENach = async (applicationId, financeUserId, payload) => {
  try {
    const appQuery = query(collection(db, 'patientApplications'), where('applicationId', '==', applicationId));
    const appSnap = await getDocs(appQuery);
    if (appSnap.empty) return { success: false, error: 'Application not found' };
    const appDoc = appSnap.docs[0];
    const appRef = doc(db, 'patientApplications', appDoc.id);

    const updateData = {
      'workflow.enach': {
        status: 'initiated',
        link: payload?.link || '',
        comments: payload?.comments || '',
        submittedBy: financeUserId,
        submittedAt: Timestamp.now()
      },
      updatedAt: Timestamp.now()
    };

    await updateDoc(appRef, updateData);

    // Notification to hospital (if hospitalId present)
    try {
      const patientName = appDoc.data()?.patient?.name || 'Patient';
      const appIdStr = applicationId;
      const hospitalUserId = appDoc.data()?.createdBy || appDoc.data()?.hospitalUserId; // heuristic
      if (hospitalUserId && createNotification) {
        await createNotification({
          recipientId: hospitalUserId,
          recipientType: 'user',
          type: 'application',
          subType: 'enach_initiated',
          title: 'eNach Initiated',
          message: `eNach process initiated for ${patientName} (ID: ${appIdStr}). Please complete eNach when done.`,
          applicationId: appIdStr,
          priority: 'normal'
        });
      }
    } catch (notifyErr) {
      console.warn('eNach initiation notification failed:', notifyErr);
    }

    return { success: true, message: 'eNach submitted' };
  } catch (error) {
    console.error('Error submitting eNach:', error);
    return { success: false, error: error.message };
  }
};

export const markENachCompleted = async (applicationId, hospitalUserId, hospitalProof = null) => {
  try {
    const appQuery = query(collection(db, 'patientApplications'), where('applicationId', '==', applicationId));
    const appSnap = await getDocs(appQuery);
    if (appSnap.empty) return { success: false, error: 'Application not found' };
    const appDoc = appSnap.docs[0];
    const appRef = doc(db, 'patientApplications', appDoc.id);

    const enach = appDoc.data()?.workflow?.enach || {};
    if (!enach.status || enach.status !== 'initiated') {
      return { success: false, error: 'eNach not in initiated state' };
    }

    const updateData = {
      'workflow.enach.status': 'completed',
      'workflow.enach.hospitalCompletedBy': hospitalUserId,
      'workflow.enach.hospitalCompletedAt': Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    // Persist optional hospital proof by uploading to Firebase Storage
    if (hospitalProof && typeof hospitalProof === 'object') {
      try {
        const fileName = hospitalProof.name || 'proof';
        const safeName = String(fileName).replace(/[^a-zA-Z0-9.-]/g, '_');
        const path = `enach/${applicationId}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${safeName}`;
        const uploadRes = await uploadFile(hospitalProof, path);
        updateData['workflow.enach.hospitalProof'] = {
          url: uploadRes.downloadURL,
          path: uploadRes.metadata.fullPath,
          name: uploadRes.metadata.name || safeName,
          size: uploadRes.metadata.size,
          type: uploadRes.metadata.type,
          uploadedAt: Timestamp.now()
        };
      } catch (uploadErr) {
        console.warn('eNach proof upload failed, proceeding without file:', uploadErr);
      }
    }

    await updateDoc(appRef, updateData);

    // Notify finance team (could broadcast or specific user if tracked)
    try {
      const patientName = appDoc.data()?.patient?.name || 'Patient';
      const appIdStr = applicationId;
      // If there is a finance processor record we could notify them.
      const financeProcessor = appDoc.data()?.workflow?.dischargeSettlement?.processedBy;
      if (financeProcessor && createNotification) {
        await createNotification({
          recipientId: financeProcessor,
          recipientType: 'user',
          type: 'application',
          subType: 'enach_completed',
          title: 'eNach Completed',
          message: `Hospital marked eNach completed for ${patientName} (ID: ${appIdStr}).`,
          applicationId: appIdStr,
          priority: 'normal'
        });
      }
    } catch (notifyErr) {
      console.warn('eNach completion notification failed:', notifyErr);
    }

    return { success: true, message: 'eNach marked completed' };
  } catch (error) {
    console.error('Error marking eNach completed:', error);
    return { success: false, error: error.message };
  }
};
export const generateDisbursalLetter = async (applicationId, financialTeamId) => {
  try {
    const appQuery = query(
      collection(db, "patientApplications"),
      where("applicationId", "==", applicationId)
    );
    const appSnapshot = await getDocs(appQuery);

    if (appSnapshot.empty) {
      return { success: false, error: "Application not found" };
    }

    const appData = appSnapshot.docs[0].data();

    // Generate PDF letter content (you can use libraries like jsPDF)
    const letterData = {
      applicationId: appData.applicationId,
      patientName: appData.patient?.name,
      hospitalName: appData.hospitalName,
      disbursalAmount: appData.workflow?.dischargeSettlement?.finalSettlementAmount || 0,
      lenderDetails: appData.workflow?.financialVerification?.lenderDetails,
      generatedBy: financialTeamId,
      generatedAt: Timestamp.now()
    };

    // Create disbursal letter record
    const letterRef = await addDoc(collection(db, "disbursalLetters"), {
      ...letterData,
      status: "generated",
      type: "disbursal_authorization"
    });

    // Update application status
    const appRef = doc(db, "patientApplications", appSnapshot.docs[0].id);
    await updateDoc(appRef, {
      'workflow.disbursal.letterGenerated': true,
      'workflow.disbursal.letterId': letterRef.id,
      'workflow.disbursal.letterGeneratedAt': Timestamp.now(),
      updatedAt: Timestamp.now()
    });

    return {
      success: true,
      letterId: letterRef.id,
      message: "Disbursal letter generated successfully"
    };
  } catch (error) {
    console.error("Error generating disbursal letter:", error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get applications processed by financial team member
export const getFinancialProcessedApplications = async (financialTeamId, filters = {}, page = 1, pageSize = 10) => {
  try {
    let q = collection(db, "patientApplications");

    // Base filter - applications processed by this financial team member
    q = query(q, where("workflow.financialVerification.verifiedBy", "==", financialTeamId));

    // Apply status filter if provided
    if (filters.status && filters.status !== "") {
      q = query(q, where("workflow.financialVerification.status", "==", filters.status));
    }

    // Order by processing date (newest first)
    q = query(q, orderBy("workflow.financialVerification.verifiedAt", "desc"));

    const querySnapshot = await getDocs(q);
    let applications = [];

    querySnapshot.forEach((doc) => {
      applications.push({
        id: doc.id,
        ...doc.data()
      });
    });

    // Client-side filtering for search term and date ranges
    if (filters.searchTerm && filters.searchTerm.trim() !== "") {
      const searchTerm = filters.searchTerm.toLowerCase();
      applications = applications.filter(app =>
        app.applicationId?.toLowerCase().includes(searchTerm) ||
        app.patient?.name?.toLowerCase().includes(searchTerm) ||
        app.policy?.number?.toLowerCase().includes(searchTerm)
      );
    }

    // Date range filtering for processing date
    if (filters.dateFrom && filters.dateFrom !== "") {
      const fromDate = new Date(filters.dateFrom);
      applications = applications.filter(app => {
        const processedDate = app.workflow?.financialVerification?.verifiedAt?.toDate();
        return processedDate && processedDate >= fromDate;
      });
    }

    if (filters.dateTo && filters.dateTo !== "") {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      applications = applications.filter(app => {
        const processedDate = app.workflow?.financialVerification?.verifiedAt?.toDate();
        return processedDate && processedDate <= toDate;
      });
    }

    // Pagination
    const total = applications.length;
    const startIndex = (page - 1) * pageSize;
    const paginatedApplications = applications.slice(startIndex, startIndex + pageSize);

    return {
      success: true,
      applications: paginatedApplications,
      total,
      page,
      pageSize,
      totalPages: Math.ceil(total / pageSize)
    };
  } catch (error) {
    console.error("Error getting processed applications:", error);
    return {
      success: false,
      error: error.message,
      applications: [],
      total: 0
    };
  }
};

// Get performance metrics for financial team member
export const getFinancialPerformanceMetrics = async (financialTeamId, dateRange = null) => {
  try {
    let q = collection(db, "patientApplications");
    q = query(q, where("workflow.financialVerification.verifiedBy", "==", financialTeamId));

    if (dateRange && dateRange.start && dateRange.end) {
      q = query(
        q,
        where("workflow.financialVerification.verifiedAt", ">=", Timestamp.fromDate(new Date(dateRange.start))),
        where("workflow.financialVerification.verifiedAt", "<=", Timestamp.fromDate(new Date(dateRange.end)))
      );
    }

    const querySnapshot = await getDocs(q);

    const metrics = {
      totalProcessed: 0,
      approved: 0,
      rejected: 0,
      pendingInfo: 0,
      averageProcessingTime: 0,
      totalProcessingTime: 0,
      emergencyProcessed: 0,
      complexCases: 0,
      approvalRate: 0,
      totalDisbursedAmount: 0,
      totalClaimAmount: 0,
      avgDailyProcessing: 0,
      disbursalEfficiency: 0
    };

    let totalProcessingTime = 0;
    let totalDisbursed = 0;
    let totalClaims = 0;
    const processingDates = new Set();

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      metrics.totalProcessed++;

      const verificationStatus = data.workflow?.financialVerification?.status;
      switch (verificationStatus) {
        case "approved":
          metrics.approved++;
          break;
        case "rejected":
          metrics.rejected++;
          break;
        case "pending_info":
          metrics.pendingInfo++;
          break;
      }

      if (data.emergency) {
        metrics.emergencyProcessed++;
      }

      if (data.priority === "urgent" || (data.workflow?.policyVerification?.approvedCoverageAmount || 0) > 500000) {
        metrics.complexCases++;
      }

      const processingTime = data.workflow?.financialVerification?.processingTimeMinutes;
      if (processingTime) {
        totalProcessingTime += processingTime;
      }

      const disbursedAmount = data.workflow?.financialVerification?.finalDisbursalAmount || 0;
      const claimAmount = data.medical?.actualCost || data.medical?.estimatedCost || 0;
      totalDisbursed += disbursedAmount;
      totalClaims += claimAmount;

      // Track unique processing dates
      const processedDate = data.workflow?.financialVerification?.verifiedAt?.toDate();
      if (processedDate) {
        processingDates.add(processedDate.toDateString());
      }
    });

    metrics.averageProcessingTime = metrics.totalProcessed > 0
      ? Math.round(totalProcessingTime / metrics.totalProcessed)
      : 0;
    metrics.totalProcessingTime = totalProcessingTime;
    metrics.totalDisbursedAmount = totalDisbursed;
    metrics.totalClaimAmount = totalClaims;

    // Calculate approval rate
    metrics.approvalRate = metrics.totalProcessed > 0
      ? Math.round((metrics.approved / metrics.totalProcessed) * 100)
      : 0;

    // Calculate disbursal efficiency
    metrics.disbursalEfficiency = totalClaims > 0
      ? Math.round((totalDisbursed / totalClaims) * 100)
      : 0;

    // Calculate average daily processing
    metrics.avgDailyProcessing = processingDates.size > 0
      ? Math.round(metrics.totalProcessed / processingDates.size)
      : 0;

    return {
      success: true,
      metrics
    };
  } catch (error) {
    console.error("Error getting financial performance metrics:", error);
    return {
      success: false,
      error: error.message,
      metrics: null
    };
  }
};

// Check if application has unresolved queries that would block financial review
// options.department: filter queries by department (e.g., 'financial', 'policy', 'medical')
// By default, checks only financial team queries for role-specific blocking
export const checkApplicationQueryStatus = async (applicationId, options = {}) => {
  try {
    // Import checkApplicationDisputeStatus from disputeService
    const { checkApplicationDisputeStatus } = await import('./disputeService');
    
    // Default to financial-scoped disputes (only check financial team queries)
    const checkOptions = options.department ? options : { department: 'financial' };
    const result = await checkApplicationDisputeStatus(applicationId, checkOptions);
    
    if (!result.success) {
      return {
        success: false,
        error: result.error,
        hasUnresolvedQueries: false,
        canProceed: false
      };
    }
    
    return {
      success: true,
      hasUnresolvedQueries: result.hasActiveDisputes,
      unresolvedQueries: result.activeDisputes,
      canProceed: result.canProceed,
      queryCount: result.activeDisputes?.length || 0
    };
  } catch (error) {
    console.error("Error checking application query status:", error);
    return {
      success: false,
      error: error.message,
      hasUnresolvedQueries: false,
      canProceed: false
    };
  }
};

// Get applications that are on hold due to unresolved queries (for financial team)
export const getFinancialApplicationsOnHold = async (
  filters = {},
  page = 1,
  pageSize = 10
) => {
  try {
    // Get applications with dispute_pending status that would normally be in financial review
    let q = query(
      collection(db, "patientApplications"),
      where("status", "==", "dispute_pending"),
      orderBy("updatedAt", "desc")
    );

    const querySnapshot = await getDocs(q);
    let applications = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Only include applications that would be in financial phase
      if (data.workflow?.financialVerification?.status === "pending" || 
          data.status === "medical_approved" || 
          data.status === "approved_for_process") {
        applications.push({
          id: doc.id,
          ...data,
        });
      }
    });

    // For each application, get the active queries/disputes
    for (let app of applications) {
      try {
        const queryStatus = await checkApplicationQueryStatus(app.applicationId);
        app.queryStatus = queryStatus;
      } catch (error) {
        console.error(`Error getting query status for ${app.applicationId}:`, error);
        app.queryStatus = {
          hasUnresolvedQueries: false,
          unresolvedQueries: [],
          canProceed: false,
          queryCount: 0
        };
      }
    }

    // Apply client-side filtering
    if (filters.searchTerm && filters.searchTerm.trim() !== "") {
      const searchTerm = filters.searchTerm.toLowerCase();
      applications = applications.filter(
        (app) =>
          app.applicationId?.toLowerCase().includes(searchTerm) ||
          app.patient?.name?.toLowerCase().includes(searchTerm) ||
          app.medical?.diagnosis?.toLowerCase().includes(searchTerm) ||
          app.hospitalName?.toLowerCase().includes(searchTerm)
      );
    }

    // Pagination
    const total = applications.length;
    const startIndex = (page - 1) * pageSize;
    const paginatedApplications = applications.slice(
      startIndex,
      startIndex + pageSize
    );

    return {
      success: true,
      applications: paginatedApplications,
      total,
      page,
      pageSize,
      totalPages: Math.ceil(total / pageSize),
    };
  } catch (error) {
    console.error("Error getting financial applications on hold:", error);
    return {
      success: false,
      error: error.message,
      applications: [],
      total: 0,
    };
  }
};

// Enhanced function to check if application can proceed to financial review
// For financial team: only check queries raised BY financial team
export const canApplicationProceedToFinancial = async (applicationId) => {
  try {
    // Check for unresolved queries raised by financial team only
    const queryStatus = await checkApplicationQueryStatus(applicationId, { department: 'financial' });
    
    if (!queryStatus.success) {
      return {
        success: false,
        canProceed: false,
        reason: "Error checking query status",
        error: queryStatus.error
      };
    }

    if (queryStatus.hasUnresolvedQueries) {
      return {
        success: true,
        canProceed: false,
        reason: "Application is on hold due to unresolved queries raised by financial team",
        unresolvedQueries: queryStatus.unresolvedQueries,
        queryCount: queryStatus.queryCount,
        phase: "financial"
      };
    }

    return {
      success: true,
      canProceed: true,
      reason: "No blocking queries found from financial team",
      phase: "financial"
    };
  } catch (error) {
    console.error("Error checking if application can proceed to financial review:", error);
    return {
      success: false,
      canProceed: false,
      reason: "Error during validation",
      error: error.message
    };
  }
};